// API 客户端
import { useEffect, useState, useRef, useCallback } from 'react';
import type {
  Need, Review, JD, Channel, ChannelPost, ResumeFilterItem,
  Candidate, Interview, Evaluation, Assessment, AssessmentRecord,
  SalaryApproval, BgCheck, Offer, PreOnboard, OnboardChecklistItem,
  NewHire, Probation, ProbationLog, FunnelItem, ChannelAnalysis,
  Budget, ActivityItem, DashboardData
} from '../types';
import { feishuNeeds, feishuCandidates } from '../data/feishuData';
import {
  deriveReviews, deriveJds, deriveChannels, deriveChannelPosts,
  deriveChannelAnalysis, deriveBudget, deriveResumeFilter,
  deriveInterviews, deriveEvaluations, deriveAssessments,
  deriveAssessmentRecords, deriveSalaryApprovals, deriveBgChecks,
  deriveOffers, derivePreOnboard, deriveOnboardChecklist,
  deriveNewHires, deriveProbations, deriveProbationLogs,
  deriveFunnel, deriveActivity,
} from '../data/derived';

const BASE = '/api';

async function request<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`);
  if (!res.ok) {
    throw new Error(`API ${path} failed: ${res.status}`);
  }
  const json: { data: T; ok: boolean } = await res.json();
  return json.data;
}

// 通用写操作请求（POST/PUT/PATCH/DELETE）
async function send<T>(method: string, path: string, body?: Record<string, any>): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const json: any = await res.json().catch(() => ({}));
  if (!res.ok || !json.ok) {
    throw new Error(json.error || `API ${path} failed: ${res.status}`);
  }
  return json.data;
}


// Build dashboard stats from real Feishu data
function buildDashboard(): DashboardData {
  const needs = feishuNeeds;
  const cands = feishuCandidates;
  const inProgress = needs.filter(n => n.status === '招聘中' || n.status === '待招聘').length;
  const totalHC = needs.reduce((s, n) => s + n.count, 0);
  const onboarded = needs.filter(n => n.status === '已入职').length;
  const interviews = cands.filter(c => c.stage === '面试中' || c.stage === '通过').length;
  const offers = needs.filter(n => n.status === '已发Offer' || n.status === '已入职').length;

  const stages = ['初筛', '面试', '终面', 'Offer', '入职'];
  const stageCounts = [
    cands.length,
    cands.filter(c => c.stage !== '淘汰' && c.stage !== '').length,
    cands.filter(c => c.hrReview === '通过').length,
    offers,
    onboarded,
  ];

  return {
    hero: {
      greeting: 'ATS RECRUITMENT COCKPIT',
      title: `${totalHC} 个在招 HC · ${cands.length} 份候选人简历`,
      stats: {
        onboarding: onboarded,
        needs: needs.length,
        resumes: cands.length,
        offers,
        avgDays: (() => { const dated = needs.filter(n => n.startDate && n.endDate); if (dated.length === 0) return 28; const total = dated.reduce((s, n) => { const d1 = new Date(n.startDate); const d2 = new Date(n.endDate); return s + Math.max(1, Math.round((d2.getTime() - d1.getTime()) / 86400000)); }, 0); return Math.round(total / dated.length); })(),
      },
    },
    kpi: {
      inProgress,
      interviews,
      talentPool: cands.length,
      passRate: cands.length > 0 ? Math.round((cands.filter(c => c.stage === "通过").length / cands.length) * 100) : 0,
    },
    funnel: deriveFunnel(),
    activity: deriveActivity(),
  };
}

export const api = {
  health: () => request<{ app: string; version: string; time: string }>('/health'),

  dashboard: () => Promise.resolve(buildDashboard()),
  needs: () => Promise.resolve(feishuNeeds as Need[]),
  reviews: () => Promise.resolve(deriveReviews()),
  jds: () => Promise.resolve(deriveJds()),

  channels: () => Promise.resolve(deriveChannels()),
  channelPosts: () => Promise.resolve(deriveChannelPosts()),
  channelAnalysis: () => Promise.resolve(deriveChannelAnalysis()),

  resumeFilter: () => Promise.resolve(deriveResumeFilter()),
  candidates: () => Promise.resolve(feishuCandidates as unknown as Candidate[]),

  interviews: () => Promise.resolve(deriveInterviews()),
  evaluations: () => Promise.resolve(deriveEvaluations()),
  assessments: () => Promise.resolve(deriveAssessments()),
  assessmentRecords: () => Promise.resolve(deriveAssessmentRecords()),

  salaryApprovals: () => Promise.resolve(deriveSalaryApprovals()),
  bgChecks: () => Promise.resolve(deriveBgChecks()),
  offers: () => Promise.resolve(deriveOffers()),

  preOnboard: () => Promise.resolve(derivePreOnboard()),
  onboardChecklist: () => Promise.resolve(deriveOnboardChecklist()),
  newHires: () => Promise.resolve(deriveNewHires()),

  probations: () => Promise.resolve(deriveProbations()),
  probationLogs: () => Promise.resolve(deriveProbationLogs()),

  funnel: () => Promise.resolve(deriveFunnel()),
  budget: () => Promise.resolve(deriveBudget()),
  activity: () => Promise.resolve(deriveActivity()),

  // ===== AI 能力 =====
  aiChat: async (messages: { role: string; content: string }[]) => {
    const res = await fetch('/api/ai/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages }),
    });
    const json: any = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) throw new Error(json.error || 'AI 请求失败');
    return json.data.result as string;
  },

  aiInsights: async (metrics: any) => {
    const res = await fetch('/api/ai/insights', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ metrics }),
    });
    const json: any = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) throw new Error(json.error || 'AI 请求失败');
    return json.data.result as string;
  },

  aiOfferDraft: async (candidate: any) => {
    const res = await fetch('/api/ai/offer-draft', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ candidate }),
    });
    const json: any = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) throw new Error(json.error || 'AI 请求失败');
    return json.data.result as string;
  },

  aiProbationReview: async (probation: any) => {
    const res = await fetch('/api/ai/probation-review', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ probation }),
    });
    const json: any = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) throw new Error(json.error || 'AI 请求失败');
    return json.data.result as string;
  },
};

// ====== 写操作（增删改）======
// 订阅/失效机制：视图通过 useApi(loader, deps, slug) 订阅某表，
// 写操作完成后自动 invalidate(slug) 触发对应视图刷新。
type Subscriber = { table?: string; fn: () => void };
const subscribers = new Set<Subscriber>();

export function invalidate(table?: string) {
  for (const sub of subscribers) {
    if (!table || !sub.table || sub.table === table) sub.fn();
  }
}

export const mutate = {
  create: (slug: string, body: Record<string, any>) =>
    send<{ id: string | number }>('POST', `/${slug}`, body).then((r) => {
      invalidate(slug);
      return r;
    }),
  update: (slug: string, id: string | number, body: Record<string, any>) =>
    send<{ updated: boolean }>('PUT', `/${slug}/${id}`, body).then((r) => {
      invalidate(slug);
      return r;
    }),
  remove: (slug: string, id: string | number) =>
    send<{ deleted: boolean }>('DELETE', `/${slug}/${id}`).then((r) => {
      invalidate(slug);
      return r;
    }),
};

// 通用 Hook - 加载数据；传入 table 可在写操作后自动刷新
export function useApi<T>(loader: () => Promise<T>, deps: any[] = [], table?: string) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reloadRef = useRef<() => void>(() => {});

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    loader()
      .then((d) => { if (!cancelled) { setData(d); setError(null); } })
      .catch((e) => { if (!cancelled) setError(String(e?.message ?? e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    reloadRef.current = load;
    const cleanup = load();
    return cleanup;
  }, [load]);

  useEffect(() => {
    if (!table) return;
    const sub: Subscriber = { table, fn: () => reloadRef.current() };
    subscribers.add(sub);
    return () => { subscribers.delete(sub); };
  }, [table]);

  const reload = () => reloadRef.current();
  return { data, loading, error, reload };
}